/**
 * Admin: send Web Push notifications.
 *
 * Features:
 *  - Compose: title (max 80), body (max 200), optional URL & icon
 *  - Live phone preview that updates as you type
 *  - Subscribers stats + recent campaigns table
 *  - Confirmation before broadcasting
 */
import React, { useEffect, useState } from 'react';
import { ArrowLeft, Bell, Send, Users, Smartphone } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_TITLE = 80;
const MAX_BODY = 200;

const AdminPush = () => {
  const { language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('/');
  const [sending, setSending] = useState(false);

  const loadStats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/push/subscribers`);
      setStats(data);
    } catch (e) {
      toast.error('Errore caricamento statistiche');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); }, []);

  // Normalise the URL the same way the backend does, so the admin sees a
  // preview of what will actually be saved. Avoids confusion with absolute
  // URLs pasted on top of the default '/'.
  const normaliseUrl = (raw) => {
    if (!raw) return '/';
    let s = String(raw).trim();
    if (!s) return '/';
    if (s.startsWith('/http')) s = s.slice(1);
    if (/^https?:\/\//i.test(s)) {
      try {
        const u = new URL(s);
        if (/(^|\.)allmx\.it$/.test(u.hostname) || /\.emergentagent\.com$/.test(u.hostname)) {
          return (u.pathname || '/') + u.search;
        }
        return '/';
      } catch (_) { return '/'; }
    }
    if (!s.startsWith('/')) s = '/' + s;
    return s.replace(/^\/+/, '/');
  };
  const normalisedUrl = normaliseUrl(url);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error(language === 'it' ? 'Titolo e testo obbligatori' : 'Title and body required');
      return;
    }
    if (!stats || stats.total === 0) {
      toast.error(language === 'it' ? 'Nessun iscritto alle notifiche' : 'No subscribers yet');
      return;
    }
    const confirm = window.confirm(
      language === 'it'
        ? `Inviare la notifica a ${stats.total} dispositivi?`
        : `Send the notification to ${stats.total} devices?`,
    );
    if (!confirm) return;

    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/push/send`, {
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || '/',
      });
      toast.success(
        language === 'it'
          ? `Inviate: ${data.sent} • Fallite: ${data.failed} • Scadute: ${data.expired_removed}`
          : `Sent: ${data.sent} • Failed: ${data.failed} • Expired: ${data.expired_removed}`,
      );
      setTitle('');
      setBody('');
      setUrl('/');
      await loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Errore invio');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="dark-container pt-20 min-h-screen">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-[7.6923%] py-8">
        <Link to="/admin/inventory" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6">
          <ArrowLeft size={20} />
          {language === 'it' ? 'Torna a Inventario' : 'Back to Inventory'}
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <Bell size={28} style={{ color: '#FF6600' }} />
          <h1 className="text-3xl font-bold">
            {language === 'it' ? 'Notifiche Push' : 'Push Notifications'}
          </h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Users size={20} className="text-blue-400" />
              <div>
                <div className="text-2xl font-bold">{loading ? '…' : stats?.total ?? 0}</div>
                <div className="text-xs text-white/60">
                  {language === 'it' ? 'Iscritti totali' : 'Total subscribers'}
                </div>
              </div>
            </div>
          </div>
          {stats && Object.entries(stats.by_platform || {}).map(([plat, count]) => (
            <div key={plat} className="bg-white/5 border border-white/10 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <Smartphone size={20} style={{ color: '#FF6600' }} />
                <div>
                  <div className="text-2xl font-bold">{count}</div>
                  <div className="text-xs text-white/60 capitalize">{plat}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Composer + Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Composer */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">
              {language === 'it' ? 'Componi notifica' : 'Compose notification'}
            </h2>

            <label className="block text-sm text-white/70 mb-1">
              {language === 'it' ? 'Titolo' : 'Title'} <span className="text-white/40">({title.length}/{MAX_TITLE})</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE))}
              placeholder={language === 'it' ? 'Es. Nuove offerte ALLMX!' : 'E.g. New ALLMX deals!'}
              className="w-full bg-black/30 border border-white/20 rounded px-3 py-2 mb-4 focus:outline-none focus:border-primary"
              data-testid="push-title-input"
            />

            <label className="block text-sm text-white/70 mb-1">
              {language === 'it' ? 'Testo' : 'Body'} <span className="text-white/40">({body.length}/{MAX_BODY})</span>
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
              rows={4}
              placeholder={language === 'it' ? 'Scopri gli sconti del weekend...' : 'Discover weekend discounts...'}
              className="w-full bg-black/30 border border-white/20 rounded px-3 py-2 mb-4 focus:outline-none focus:border-primary resize-none"
              data-testid="push-body-input"
            />

            <label className="block text-sm text-white/70 mb-1">
              {language === 'it' ? 'Link di destinazione (al click)' : 'Destination URL (on click)'}
            </label>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="/products oppure /products/lana-di-vetro"
              className="w-full bg-black/30 border border-white/20 rounded px-3 py-2 mb-2 focus:outline-none focus:border-primary"
              data-testid="push-url-input"
            />
            <div className="text-xs mb-6">
              {url && url !== normalisedUrl ? (
                <span className="text-orange-400">
                  ⚠️ {language === 'it' ? 'Sarà salvato come:' : 'Will be saved as:'}{' '}
                  <code className="bg-black/40 px-2 py-0.5 rounded">{normalisedUrl}</code>
                </span>
              ) : (
                <span className="text-white/40">
                  {language === 'it'
                    ? 'Inserisci solo il percorso (es. /products/lana-di-vetro), non l\'URL completo.'
                    : 'Enter only the path (e.g. /products/lana-di-vetro), not the full URL.'}
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !title.trim() || !body.trim() || stats?.total === 0}
              className="w-full btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="push-send-btn"
            >
              <Send size={18} />
              {sending
                ? (language === 'it' ? 'Invio in corso…' : 'Sending…')
                : (language === 'it' ? `Invia a ${stats?.total ?? 0} dispositivi` : `Send to ${stats?.total ?? 0} devices`)}
            </button>
          </div>

          {/* Live preview (phone mockup) */}
          <div>
            <h2 className="text-lg font-semibold mb-4">
              {language === 'it' ? 'Anteprima' : 'Preview'}
            </h2>
            <div className="bg-gradient-to-b from-white/10 to-white/5 border border-white/10 rounded-2xl p-6">
              <div className="bg-white text-black rounded-xl shadow-lg p-3 flex gap-3">
                <img src="/allmx-logo.png" alt="ALLMX" className="w-10 h-10 rounded-lg object-contain bg-black" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-0.5">
                    <span className="font-semibold text-gray-800">ALLMX</span>
                    <span>· {language === 'it' ? 'adesso' : 'now'}</span>
                  </div>
                  <div className="font-semibold text-sm truncate">
                    {title || (language === 'it' ? 'Titolo della notifica' : 'Notification title')}
                  </div>
                  <div className="text-sm text-gray-700 line-clamp-2">
                    {body || (language === 'it' ? 'Il testo della notifica appare qui...' : 'Notification body appears here...')}
                  </div>
                </div>
              </div>
              <p className="text-white/50 text-xs mt-4">
                {language === 'it'
                  ? 'L\'anteprima è indicativa: ogni dispositivo mostra le notifiche con uno stile leggermente diverso.'
                  : 'Preview is illustrative — each device shows notifications slightly differently.'}
              </p>
            </div>
          </div>
        </div>

        {/* Recent campaigns */}
        {stats?.recent_campaigns?.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold mb-3">
              {language === 'it' ? 'Cronologia invii' : 'Campaign history'}
            </h2>
            <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr className="text-left text-sm">
                    <th className="px-4 py-2">{language === 'it' ? 'Data' : 'Date'}</th>
                    <th className="px-4 py-2">{language === 'it' ? 'Titolo' : 'Title'}</th>
                    <th className="px-4 py-2 text-right">{language === 'it' ? 'Inviate' : 'Sent'}</th>
                    <th className="px-4 py-2 text-right">{language === 'it' ? 'Fallite' : 'Failed'}</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_campaigns.map((c, idx) => (
                    <React.Fragment key={`camp-${idx}`}>
                      <tr className="border-t border-white/5 text-sm">
                        <td className="px-4 py-2 text-white/70">
                          {c.sent_at ? new Date(c.sent_at).toLocaleString('it-IT') : ''}
                        </td>
                        <td className="px-4 py-2 text-white">{c.title}</td>
                        <td className="px-4 py-2 text-right text-green-400 font-semibold">{c.sent}</td>
                        <td className="px-4 py-2 text-right text-red-400">{c.failed}</td>
                      </tr>
                      {Array.isArray(c.failure_samples) && c.failure_samples.length > 0 && (
                        <tr className="text-xs text-red-300/70 bg-red-500/5">
                          <td colSpan="4" className="px-4 py-2">
                            <strong>Errori:</strong> {c.failure_samples.join(' | ')}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPush;
